import { getJwtToken } from "@web-src/modules/auth/utils/tokenStorage";
import store from "@web-src/store";
import { logout } from "@web-src/modules/auth/store/authSlice";
import { getAxiosInstance } from "@jugl-web/rest-api";
import { environment } from "@web-src/environments/environment";
import { anonymousSessionState } from "@web-src/modules/auth/providers/AnonymousSessionProvider/anonymousSessionState";
import axios, { InternalAxiosRequestConfig } from "axios";
import { rateLimitEvents } from "./rateLimitEvents";

type AnonRecoveryConfig = InternalAxiosRequestConfig & {
  __anonRefreshAttempted?: boolean;
  __anonRetryCount?: number;
};

const isSafeToRetry = (cfg: AnonRecoveryConfig | undefined) =>
  cfg?.method?.toLowerCase() === "get";

const api = getAxiosInstance(
  () => getJwtToken()?.accessToken,
  environment.apiUrl,
  environment.isDemo
);

api.interceptors.response.use(undefined, async (error) => {
  const cfg = error?.config as AnonRecoveryConfig | undefined;
  const status: number | undefined = error?.response?.status;
  const url: string | undefined = cfg?.url;
  const isAnon = anonymousSessionState.isAnonymous;
  const isAnonEndpoint = !!url?.includes("/anonymous_session");
  const isCancelled = axios.isCancel(error) || error?.code === "ERR_CANCELED";

  if (status === 401) {
    if (!isAnon) {
      store.dispatch(logout());
      throw error;
    }

    if (
      cfg &&
      !isAnonEndpoint &&
      !cfg.__anonRefreshAttempted &&
      isSafeToRetry(cfg)
    ) {
      cfg.__anonRefreshAttempted = true;
      const ok = await anonymousSessionState.refreshSession();
      if (ok) {
        return api.request(cfg);
      }
    }
  }

  const isTransient =
    !isCancelled &&
    (!error?.response ||
      (typeof status === "number" && status >= 500 && status < 600));

  if (isTransient && isAnon && cfg && !isAnonEndpoint && isSafeToRetry(cfg)) {
    cfg.__anonRetryCount = (cfg.__anonRetryCount ?? 0) + 1;
    if (cfg.__anonRetryCount <= 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, 300);
      });
      return api.request(cfg);
    }
  }

  if (status === 429 && !isAnonEndpoint) {
    rateLimitEvents.emit(isAnon ? "anonymous" : "authenticated");
  }
  throw error;
});

export default api;
